// CustomFormService.ts

import { axiosRequest } from "./AxiosService";
import { CustomFormInitializeResponse } from "../type/response/CustomFormResponse";
import { UUID } from "crypto";
import {
  FormStructure,
  Item,
  SelectParam,
  Section,
  ApplicantParam,
  ApproverParam,
  ApproverActivityParam,
} from "../pages/form/CustomFormContainer";
import dayjs from "dayjs";
import { ApplicationEntriesType } from "../pages/form/data/ApplicationEntriesType";
import { ApplicationAuditListResponse, ApplicationAuditData } from "../type/response/ApplicationAuditListResponse";

/**
 * フォームアイテム一覧を取得し、セクション構造に変換して返す
 * @param documentId - 申請書ID
 * @returns フォーム構造データ、取得失敗時はundefined
 */
export const getFormItems = async (documentId: string) => {
  const response: Promise<CustomFormInitializeResponse> = axiosRequest(
    "/customForm/getData",
    "POST",
    {
      data: {
        id: documentId,
      },
    }
  );
  if ((await response) !== undefined) {
    const formItemsList = (await response).formItemsList;

    let sectionData: Section[] = [];
    let tmpSectionData: Section = {
      id: "",
      name: "",
      itemList: [],
    };

    for (const value of formItemsList) {

      if (value.type === "section") {
        if (tmpSectionData.id) {
          // 現在のセクションを追加
          sectionData.push({ ...tmpSectionData });
        }
        // 新しいセクションを初期化
        tmpSectionData = {
          id: value.id,
          name: value.label,
          itemList: [],
        };
      } else {
        const tmpSelectItemData: SelectParam[] = [];
        for (const selectItems of value.selectItemList ?? []) {
          tmpSelectItemData.push({
            id: selectItems.id,
            value: selectItems.value,
            label: selectItems.label,
          });
        }
        const tmpItemData: Item = {
          id: value.id,
          name: value.label,
          type: value.type,
          defaultValue: value.type === "date" ? (value.defaultValue ? value.defaultValue : dayjs().format('YYYY/MM/DD') ) :value.defaultValue,
          selectedParam: tmpSelectItemData,
        };
        tmpSectionData.itemList.push(tmpItemData);
      }
    }

    // 最後のセクションを追加
    if (tmpSectionData.id) {
      sectionData.push(tmpSectionData);
    }

    //申請者情報の一覧取得
    const tmpApplicantData: ApplicantParam[] = [];
    for (const applicant of (await response).applicantList ?? []) {
      tmpApplicantData.push({ id: applicant.id, firstName: applicant.firstName, lastName: applicant.lastName});
    }

    //承認者情報の一覧取得
    const tmpApproverActivityData: ApproverActivityParam[] = [];
    const tmpApproverData: ApproverParam[] = [];
    for (const approver of (await response).approvalActivityList ?? []) {
      for (const user of (await approver).approverList ?? []){
        tmpApproverData.push({ id: user.id, lastName: user.lastName, firstName: user.firstName });
      } 
      tmpApproverActivityData.push({ id: approver.id, order: approver.order,activityName: approver.activityName, approverList: tmpApproverData});
    }

    let submitData: FormStructure = {
      id: (await response).id,
      title: (await response).title,
      subTitle: (await response).subTitle,
      applicantList: tmpApplicantData,
      approvalActivityList: tmpApproverActivityData,
      section: sectionData,
    };

    return submitData;
  } else {
    return undefined;
  }
};

/**
 * 申請エントリーリストを取得する
 * @returns 申請エントリーの配列
 */
export const fetchApplicationEntryList = async(): Promise<ApplicationEntriesType[]> => {
    try {
      const response = await axiosRequest<ApplicationAuditListResponse>(
        "/customForm/getAuditListForNumberSearch",
        "GET",
      );

      // ApplicationAuditListResponse → ApplicationEntriesType に変換
      const convertedData = (response.applicationAuditList || []).map(data => {
        // richTextタイプのデータを抽出してMapに格納
        const richTextMap: Record<string, string> = {};
        data.formEntryList
          ?.filter(f => f.type === 'richText')
          .forEach(f => {
            richTextMap[f.label] = f.value || '';
          });

        return {
          applicationEntryId: data.applicationEntryId ?? '',
          number: data.number ?? '',
          submittedAt: data.submittedAt,
          type: data.type,
          applicationName: data.applicationName,
          // 申請者名: lastName + firstName
          applicant: `${data.applicant?.lastName ?? ''} ${data.applicant?.firstName ?? ''}`,
          // 申請者情報: key: value形式で改行区切り
          userProfiles: data.applicant?.profileList?.map(p => `${p.key}: ${p.value}`).join('\n') ?? '',
          status: data.status ?? '',
          // 現在のアクティビティ: status='InProgress'のものを抽出
          currentActivity: data.activityList?.find(a => a.status === 'InProgress')?.activityName ?? '',
          // 申請内容: itemOrderで並び替えてlabel: value形式で改行区切り
          formSummary: data.formEntryList
            ?.sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0))
            .map(f => {
              // linkList タイプの場合、formLinks を使用
              if (f.type === 'linkList' && f.formLinks && f.formLinks.length > 0) {
                const links = f.formLinks
                  .sort((a, b) => a.order - b.order)
                  .map(link => `${link.description}: ${link.url}`)
                  .join('\n');
                return `${f.label}:\n${links}`;
              }
              // richText タイプの場合、labelのみ（値は前端で処理）
              if (f.type === 'richText') {
                return `${f.label}: [リッチテキスト]`;
              }
              // 通常の場合
              return `${f.label}: ${f.value}`;
            })
            .join('\n') ?? '',
          // richTextのデータを保持
          richTextMap: Object.keys(richTextMap).length > 0 ? richTextMap : undefined,
          // 添付ファイル: 有 or 無
          // formEntryListの中にtype="fileUpload"でvalueが空でないものがあれば「有」
          reportFileStatus: data.formEntryList?.some(
            f => f.type === 'fileUpload' && f.value && (Array.isArray(f.value) ? f.value.length > 0 : true)
          ) ? '有' : '無',
        };
      });

      return convertedData;

    } catch (error) {
      console.error("データ取得中にエラーが発生しました", error);
      return [];
    }
  };
